import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetLanguageFieldByIdQuery } from "@generated";
import { LanguageFieldForm } from "../LanguageFieldForm/LanguageFieldForm";
import { useLanguageFieldMutations } from "../api";
import { emptyLanguageFieldForm } from "../types";
import type { LanguageFieldFormValues } from "../types";

const FORM_ID = "language-field-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useLanguageFieldMutations();
  const { data, isLoading, refetch } = useV2GetLanguageFieldByIdQuery(
    { id: Number(id) },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<LanguageFieldFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const attrs = data?.data?.attributes;

  const loadedInitial = useMemo<LanguageFieldFormValues>(() => {
    if (!attrs) return emptyLanguageFieldForm;
    return {
      name: attrs.name ?? "",
      preamble: attrs.preamble ?? "",
      postamble: attrs.postamble ?? "",
    };
  }, [attrs]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;
  const sowLanguage = attrs?.["sow-language"] ?? "";

  const handleSubmit = async (values: LanguageFieldFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      refetch();
    } catch (err) {
      console.error("Failed to update language field", err);
      setToast({ variant: "danger", text: "Could not save language field." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Language Field"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <LanguageFieldForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
          sowLanguage={sowLanguage}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
